import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { DataSourceOrdenacaoInsensivel } from '../../../../shared/helpers/ordenacao-tabela.helper';
import { RouterModule } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '../../../../shared/components/empty-list/empty-list.component';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { RateioCentroCustoService } from '../../../../shared/services/apis/financas/rateio-centro-custo.service';
import { RateioCentroCusto } from '../../../../shared/services/models/financas/rateio-centro-custo.model';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxDefaultListComponent } from '../../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { UtilService } from '../../../../shared/services/util.service';

@Component({
  selector: 'app-rateios-centro-custo',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatCheckboxModule,
    MatMenuModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    EmptyListComponent,
    RouterModule,
    HeaderComponent,
    LoadingBoxDefaultListComponent,
  ],
  templateUrl: './rateios-centro-custo.component.html',
  styleUrl: './rateios-centro-custo.component.css',
})
export class RateiosCentroCustoComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Financas;

  public model: FormGroup;
  public _paginaAtual: number = 0;
  public _tamanhoPagina: number = 20;
  public _totalRegistros: number = 0;

  public _dataSource = new DataSourceOrdenacaoInsensivel<RateioCentroCusto>();
  public _isLoading: boolean = false;

  public displayedColumns: string[] = ['select', 'nome', 'actions'];
  public selection = new SelectionModel<RateioCentroCusto>(true, []);

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this._dataSource.sort = sort;
  }

  constructor(
    private formBuilder: FormBuilder,
    private rateioCentroCustoService: RateioCentroCustoService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private utilService: UtilService
  ) {
    this._dataSource.sortingDataAccessor = (item, property) => {
      const value = (item as any)[property];
      return typeof value === 'string' ? value.toLowerCase() : value;
    };
  }

  ngOnInit(): void {
    this.model = this.formBuilder.group({ pesquisa: [''] });
    this.loadFilters();
    this.carregarDados();
  }

  ngOnDestroy(): void {
    this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, [
      '/configuracoes/financas/rateios-centro-custo/cadastrar',
    ]);
  }

  private loadFilters(): void {
    const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (filtroSalvo) {
      this.model.patchValue(filtroSalvo);
    }
  }

  public carregarDados(reset: boolean = false): void {
    this._isLoading = true;
    const pesquisa = this.model.get('pesquisa')?.value || '';
    this.rateioCentroCustoService.getAll(pesquisa, this._paginaAtual, this._tamanhoPagina).subscribe({
      next: response => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show(
            'Aviso',
            response.errorDescription || 'Erro ao carregar rateios',
            AlertType.warning
          );
          return;
        }
        this._totalRegistros = response.totalRegistros;
        if (reset) {
          this._dataSource.data = response.listaRateios;
        } else {
          this._dataSource.data = [...this._dataSource.data, ...response.listaRateios];
        }
      },
      error: err => {
        this._isLoading = false;
        this.alertService.show('Aviso', err.message || 'Erro ao carregar rateios', AlertType.warning);
      },
    });
  }

  public proximaPagina(): void {
    this._paginaAtual++;
    this.carregarDados();
  }

  public clickPesquisar(): void {
    this._paginaAtual = 0;
    this.selection.clear();
    this._dataSource.data = [];
    this.carregarDados(true);
  }

  public isAllSelected(): boolean {
    return this.selection.selected.length === this._dataSource.data.length && this._dataSource.data.length > 0;
  }

  public toggleAllRows(): void {
    this.isAllSelected() ? this.selection.clear() : this.selection.select(...this._dataSource.data);
  }

  // ─── Exclusão individual/em massa ───
  public abrirModalExcluir(item?: RateioCentroCusto): void {
    const isBulk = !item;
    const itemsToDelete = isBulk ? this.selection.selected : [item!];
    if (itemsToDelete.length === 0) return;

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmar exclusão',
        description: isBulk
          ? 'Tem certeza que deseja excluir as configurações de rateio selecionadas?'
          : 'Tem certeza que deseja excluir esta configuração de rateio?',
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result?.confirm) return;

      this._isLoading = true;
      if (isBulk) {
        this.rateioCentroCustoService.deleteBulk(itemsToDelete.map(i => i.idRateioCentroCusto)).subscribe({
          next: response => {
            this._isLoading = false;
            if (response.isError) {
              this.alertService.show('Aviso', response.errorDescription || 'Erro ao excluir', AlertType.warning);
            } else {
              this.alertService.show(
                'Sucesso',
                'Configurações de rateio excluídas com sucesso',
                AlertType.success
              );
              this.selection.clear();
              this.clickPesquisar();
            }
          },
          error: err => {
            this._isLoading = false;
            this.alertService.show('Aviso', err.message || 'Erro ao excluir', AlertType.warning);
          },
        });
      } else {
        this.rateioCentroCustoService.delete(item!.idRateioCentroCusto).subscribe({
          next: response => {
            this._isLoading = false;
            if (response.isError) {
              this.alertService.show('Aviso', response.errorDescription || 'Erro ao excluir', AlertType.warning);
            } else {
              this.alertService.show(
                'Sucesso',
                'Configuração de rateio excluída com sucesso',
                AlertType.success
              );
              this.clickPesquisar();
            }
          },
          error: err => {
            this._isLoading = false;
            this.alertService.show('Aviso', err.message || 'Erro ao excluir', AlertType.warning);
          },
        });
      }
    });
  }
}
